import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import AudioPlayerBar from './AudioPlayerBar';
import ForcePasswordChangeModal from './ForcePasswordChangeModal';
import Footer from './Footer';
import HeaderBar from './header/HeaderBar';
import FirstPurchasePromoBanner from './purchase-promo-banner';

interface LayoutProps {
  children: React.ReactNode;
  darkTheme?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, darkTheme = false }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { currentPodcast, useFloatingPlayerChrome } = useAudioPlayer();
  const showPlayerChrome = !!currentPodcast;

  const [isVersion2, setIsVersion2] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const checkVersion2 = () => {
      setIsVersion2(document.querySelector('[data-version2="true"]') !== null);
    };

    checkVersion2();
    const interval = setInterval(checkVersion2, 100);

    return () => clearInterval(interval);
  }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    const el = headerRef.current;
    if (!el) return;

    const syncHeaderHeightVar = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      root.style.setProperty('--site-header-height', `${Math.max(48, Math.ceil(h))}px`);
    };

    syncHeaderHeightVar();
    const ro = new ResizeObserver(syncHeaderHeightVar);
    ro.observe(el);
    window.addEventListener('resize', syncHeaderHeightVar);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncHeaderHeightVar);
    };
  }, [location.pathname]);

  /** فقط پلیر ویدیو/صوت دوره تیره؛ صفحه جزئیات دوره روشن است */
  const isCoursesDarkSurface = /^\/courses\/[^/]+\/(videos|audios)\//.test(location.pathname);
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

  const shouldUseDarkTheme = darkTheme || isVersion2 || isCoursesDarkSurface;

  if (isAuthPage) {
    return (
      <div className="min-h-[100dvh] bg-[#F8FAFC]">
        {children}
        <ForcePasswordChangeModal isOpen={user?.mustChangePassword === true} />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex flex-col ${
        shouldUseDarkTheme ? (isCoursesDarkSurface ? 'bg-[#0a0a0a]' : 'bg-[#040404]') : 'bg-gray-50'
      }`}
    >
      <header ref={headerRef} className="sticky top-0 z-50 min-w-0 overflow-x-clip">
        <FirstPurchasePromoBanner />
        <HeaderBar />
      </header>

      <main className={`flex-1 ${showPlayerChrome ? 'pb-20 sm:pb-24' : ''}`}>{children}</main>

      <Footer />

      <AudioPlayerBar />

      <ForcePasswordChangeModal isOpen={user?.mustChangePassword === true} />
    </div>
  );
};

export default Layout;
